import React, { useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { formatCurrency, formatDateOnly } from "../Opportunities/helpers";
import { i18n } from "../../translate/i18n";
import { getConflictText } from "../../utils/contactConflict";
import api from "../../services/api";
import {
  decidePhoneSource,
  normalizeBrazilianPhoneFrontend,
  phoneLabelState,
} from "../../utils/phoneSource";

/**
 * Erro a mostrar enquanto a pessoa digita, ou null quando está tudo certo.
 * Campo vazio não é erro aqui: quem exige o preenchimento é o botão
 * "Efetivar" — sair sem telefone é um caminho deliberado, pelo botão
 * secundário.
 */
export function validatePhone(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return null;

  if (/[^\d()\s-]/.test(trimmed)) {
    return "Digite apenas números, sem letras nem outros símbolos.";
  }
  if (!normalizeBrazilianPhoneFrontend(trimmed)) {
    return "Telefone inválido — informe DDD + número (ex: 48999203266).";
  }
  return null;
}

/** Só com número válido o botão "Efetivar" libera. */
export const isPhoneValid = (raw) =>
  Boolean(String(raw || "").trim()) && !validatePhone(raw);

const useStyles = makeStyles((theme) => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  hint: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    display: "block",
  },
  semContato: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

const Linha = ({ classes, rotulo, valor }) => (
  <>
    <Typography variant="body2" className={classes.label}>
      {rotulo}
    </Typography>
    <Typography variant="body2">{valor || "—"}</Typography>
  </>
);

/**
 * Confirmação antes de efetivar: mostra o que a IA extraiu do PDF para
 * conferência humana e pede o telefone.
 *
 * O telefone nasce VAZIO de propósito. A apólice em PDF não traz telefone do
 * segurado de forma confiável — os números impressos são da seguradora (SAC,
 * ouvidoria, assistência 24h) — e um número errado aqui vira o destino das
 * mensagens de WhatsApp do cliente. Preenchimento é humano ou nada.
 *
 * Seguir sem telefone continua possível, mas pelo botão secundário: o caminho
 * existe para quando o número realmente não está à mão, não para ser o
 * desfecho de quem só apertou "Efetivar" com o campo em branco.
 */
const ConfirmEfetivarModal = ({
  open,
  documento,
  saving,
  conflito,
  onClose,
  onConfirm,
  onVincularExistente,
  onEfetivarSemContato,
}) => {
  const classes = useStyles();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(null);

  const [cadastro, setCadastro] = useState(null);
  const [loadingCadastro, setLoadingCadastro] = useState(false);
  const cadastroRef = useRef(null);
  const editedRef = useRef(false);
  const documentoId = documento?.id;

  // Celular do cadastro da Carteira (GET). Qualquer falha — 404, 401, 500,
  // rede, resposta inesperada — cai em silêncio no comportamento anterior
  // (sugestão do PDF ou vazio). Declarado antes do efeito do valor inicial: ao
  // abrir, ele zera o estado do cadastro antes de o outro calcular o valor.
  useEffect(() => {
    cadastroRef.current = null;
    editedRef.current = false;
    setCadastro(null);
    if (!open || !documentoId) {
      setLoadingCadastro(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingCadastro(true);
    api
      .get(`/policy-document-imports/${documentoId}/celular-cadastro`, { timeout: 8000 })
      .then((response) => {
        if (cancelled) return;
        const data = response && response.data;
        const celular = data && data.origem === "cadastro" && typeof data.celular === "string"
          ? data.celular.trim()
          : "";
        if (celular) {
          cadastroRef.current = celular;
          setCadastro(celular);
          // Se o operador já digitou algo antes da resposta, não sobrescreve.
          if (!editedRef.current) {
            setPhone(celular);
            setPhoneError(validatePhone(celular));
          }
        }
        setLoadingCadastro(false);
      })
      .catch(() => {
        if (!cancelled) setLoadingCadastro(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, documentoId]);

  useEffect(() => {
    if (open) {
      // Sugestão da IA: celular tem prioridade sobre fixo. Vem só quando o PDF
      // rotulou de quem é o número, então na maioria das apólices continua
      // vazio. O campo segue editável e o modal abre igual — quem confirma é
      // sempre o operador.
      const { value } = decidePhoneSource({
        cadastro: cadastroRef.current,
        pdfPhone: documento?.extractedPhone,
        pdfPhoneFixed: documento?.extractedPhoneFixed,
      });
      setPhone(value);
      setPhoneError(validatePhone(value));
    }
    // Depende dos campos, não do objeto inteiro: reabrir o mesmo documento não
    // precisa refazer a sugestão por causa de mudança em outra coluna.
  }, [open, documento?.extractedPhone, documento?.extractedPhoneFixed, documento?.extractedPhoneBlocked]);

  if (!documento) return null;

  // Vincular só faz sentido com contato(s) identificado(s) e fora do caso de
  // contato duplicado (varios_nomes_reais: resolver o duplicado primeiro).
  const ids = conflito?.contactIds || [];
  const podeVincular = Boolean(conflito) && conflito.reason !== "varios_nomes_reais" && ids.length > 0;

  const labelState = cadastro && phone.trim() ? phoneLabelState(phone, cadastro) : null;
  const registryHelper =
    labelState === "cadastro"
      ? i18n.t("policyImport.phoneSource.helperCadastro")
      : labelState === "alterado"
      ? i18n.t("policyImport.phoneSource.helperAlterado")
      : null;

  const handleConfirm = () => {
    const error = validatePhone(phone);
    if (error) {
      setPhoneError(error);
      return;
    }
    // Só o estado "alterado" (veio do cadastro e foi trocado por outro número
    // válido) pede para gravar no cadastro; nos demais a chave nem é enviada.
    onConfirm(
      labelState === "alterado"
        ? { phone: phone.trim(), replaceCadastroPhone: true }
        : { phone: phone.trim() || undefined }
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Conferir antes de efetivar</DialogTitle>
      <DialogContent dividers>
        {conflito ? (
          <>
            <Typography variant="body1" gutterBottom>
              {getConflictText(conflito.reason, i18n.t.bind(i18n))}
            </Typography>
            {conflito.contactName ? (
              <Typography variant="body2" gutterBottom>
                {i18n.t("contactConflict.existingContact", {
                  name: conflito.contactName,
                  interpolation: { escapeValue: false },
                })}
              </Typography>
            ) : null}
            {podeVincular ? (
              <Typography variant="body2" className={classes.label}>
                {conflito.contactIds.length > 1
                  ? i18n.t("contactConflict.chooseContact")
                  : "Deseja vincular a apólice a este contato? O cadastro dele não será alterado."}{" "}
                Se preferir, dá para efetivar sem contato e vincular depois pelo Kanban.
              </Typography>
            ) : (
              <Typography variant="body2" className={classes.label}>
                Dá para efetivar sem contato e vincular depois pelo Kanban.
              </Typography>
            )}
          </>
        ) : null}

        <div className={classes.grid} style={conflito ? { marginTop: 16 } : undefined}>
          <Linha classes={classes} rotulo="Cliente" valor={documento.clientNameRaw} />
          <Linha classes={classes} rotulo="CPF/CNPJ" valor={documento.cpfCnpj} />
          <Linha classes={classes} rotulo="Produto" valor={documento.product} />
          <Linha classes={classes} rotulo="Seguradora" valor={documento.insurer} />
          <Linha
            classes={classes}
            rotulo="Vigência"
            valor={
              documento.startDate || documento.endDate
                ? `${formatDateOnly(documento.startDate)} a ${formatDateOnly(documento.endDate)}`
                : null
            }
          />
          <Linha
            classes={classes}
            rotulo="Prêmio"
            valor={
              documento.premium !== null && documento.premium !== undefined
                ? formatCurrency(documento.premium)
                : null
            }
          />
          <Linha classes={classes} rotulo="Nº apólice" valor={documento.policyNumber} />
          <Linha classes={classes} rotulo="Item segurado" valor={documento.insuredAssetId} />
        </div>

        {conflito ? null : (
        <TextField
          id="efetivar-phone"
          fullWidth
          size="small"
          variant="outlined"
          label="Telefone / celular do cliente"
          placeholder="48999203266"
          value={phone}
          disabled={saving || loadingCadastro}
          error={Boolean(phoneError)}
          helperText={phoneError || registryHelper}
          InputProps={
            loadingCadastro || labelState
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      {loadingCadastro ? <CircularProgress size={16} /> : null}
                      {labelState ? (
                        <Chip
                          size="small"
                          label={i18n.t(
                            labelState === "cadastro"
                              ? "policyImport.phoneSource.badgeCadastro"
                              : "policyImport.phoneSource.badgeAlterado"
                          )}
                        />
                      ) : null}
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          onChange={(event) => {
            const valor = event.target.value;
            editedRef.current = true;
            setPhone(valor);
            // Valida a cada tecla: o operador corrige na hora, em vez de
            // descobrir o problema só ao submeter.
            setPhoneError(validatePhone(valor));
          }}
        />
        )}
        {conflito || !documento?.extractedPhoneBlocked ? null : (
          <Typography variant="caption" className={classes.hint} style={{ color: "#EF6C00" }}>
            Telefone da corretora detectado — informe o do cliente.
          </Typography>
        )}

        {conflito ? null : (
          <Typography variant="caption" className={classes.hint}>
            Digite apenas números — DDD + número (ex: 48999203266).{" "}
            {cadastro
              ? ""
              : documento?.extractedPhone || documento?.extractedPhoneFixed
              ? "O número acima foi lido do PDF — confira antes de efetivar."
              : "A apólice em PDF raramente traz o telefone do segurado, por isso o campo vem vazio."}
          </Typography>
        )}

        {conflito ? null : (
          <div className={classes.semContato}>
            <Button size="small" onClick={onEfetivarSemContato} disabled={saving}>
              Efetivar sem contato
            </Button>
            <Typography variant="caption" className={classes.label}>
              Use quando o telefone não estiver disponível. A apólice entra sem contato e você
              vincula depois pelo Kanban.
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {conflito ? (
          <>
            <Button onClick={onClose} disabled={saving}>
              Voltar
            </Button>
            <Button onClick={onEfetivarSemContato} disabled={saving}>
              Efetivar sem telefone
            </Button>
            {podeVincular && ids.length === 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => onVincularExistente(ids[0])}
                disabled={saving}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : i18n.t("contactConflict.linkHere")}
              </Button>
            ) : null}
            {podeVincular && ids.length > 1
              ? ids.map((id) => (
                  <Button
                    key={id}
                    variant="contained"
                    color="primary"
                    onClick={() => onVincularExistente(id)}
                    disabled={saving}
                  >
                    {i18n.t("contactConflict.contactNumber", { id })}
                  </Button>
                ))
              : null}
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={saving || !isPhoneValid(phone)}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : "Efetivar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmEfetivarModal;
